import { useState } from 'react';
import { LuPercent, LuScissors } from 'react-icons/lu';

import { AccountType } from '@ndvr/portfolio-api';

import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { formatAccountType, formatCurrency, formatPercent, useRequiredParams } from 'utils';
import { getEffectiveTaxBudget, getTaxBudgetUsage } from 'views/account/tax-budget';
import { useAccountsYtdTaxMetrics } from 'views/account/tax-metrics-queries';

import { summarizePolicyTaxLossHarvesting } from './investment-policy-overview';
import { PolicyQueryError } from './PolicyQueryError';
import {
    PolicyTable,
    PolicyTableBody,
    PolicyTableCell,
    PolicyTableHead,
    PolicyTableHeader,
    PolicyTableRow,
} from './PolicyTable';
import { usePolicyScope } from './usePolicyScope';

const currency = (amount?: number) => (amount != null && Number.isFinite(amount) ? formatCurrency(amount) : '—');
const rate = (value: string) =>
    value != null && value !== '' && Number.isFinite(Number(value)) ? formatPercent(Number(value) * 100, 1) : '—';

export const PolicyTaxes = () => {
    const { accountGUID, accountGroupGUID } = useRequiredParams(['accountGUID', 'accountGroupGUID']);
    const scope = usePolicyScope(accountGUID, accountGroupGUID);
    const [expanded, setExpanded] = useState(true);
    const { accounts } = scope;
    const taxableAccounts = accounts.filter((account) => account.accountType === AccountType.TAXABLE);
    const metricsQuery = useAccountsYtdTaxMetrics(
        taxableAccounts.map((account) => account.accountGUID),
        { throwOnError: false },
    );
    const harvesting = summarizePolicyTaxLossHarvesting(accounts);

    return (
        <div className="space-y-4.5">
            <div className="flex min-h-14 items-center justify-between gap-4">
                <h2 id="policy-taxes-title" className="text-cocoa-1000 text-[22px] font-medium">
                    Taxes
                </h2>
            </div>
            {scope.status !== 'ready' ? (
                scope.status === 'error' ? (
                    <PolicyQueryError message="Couldn’t load tax settings." queries={scope.retryQueries} />
                ) : (
                    <p role="status" className="text-cocoa-500 text-sm">
                        Loading tax settings…
                    </p>
                )
            ) : (
                <>
                    <Card className="gap-0 overflow-hidden rounded-[10px] border-stone-200 py-0 shadow-none">
                        <button
                            type="button"
                            className="flex w-full items-center gap-3.5 px-6 py-[22px] text-left"
                            aria-expanded={expanded}
                            aria-controls="policy-tax-accounts"
                            onClick={() => setExpanded(!expanded)}
                        >
                            <LuPercent
                                aria-hidden="true"
                                className="text-cocoa-500 size-10 shrink-0 rounded-lg bg-stone-100 p-2"
                            />
                            <span className="flex-1">
                                <span className="text-cocoa-1000 block text-[15px] font-medium">Tax budget</span>
                                <span className="text-cocoa-500 mt-1 block text-sm">
                                    Limit realized tax while rebalancing. Budgets and rates are set per account.
                                </span>
                            </span>
                            <span className="text-cocoa-600 text-xs">{expanded ? 'Hide details' : 'Show details'}</span>
                        </button>
                        {expanded && (
                            <div id="policy-tax-accounts">
                                <PolicyTable
                                    containerClassName="rounded-none border-0 border-t"
                                    className="min-w-[760px]"
                                >
                                    <PolicyTableHeader>
                                        <PolicyTableRow>
                                            {[
                                                'Account',
                                                'Budget',
                                                'Realized tax YTD',
                                                'Used',
                                                'ST rate',
                                                'LT rate',
                                            ].map((label, index) => (
                                                <PolicyTableHead
                                                    key={label}
                                                    className={index ? 'text-right' : undefined}
                                                >
                                                    {label}
                                                </PolicyTableHead>
                                            ))}
                                        </PolicyTableRow>
                                    </PolicyTableHeader>
                                    <PolicyTableBody>
                                        {accounts.map((account) => {
                                            const metric = metricsQuery.data?.find(
                                                (item) => item.accountGuid === account.accountGUID,
                                            );
                                            const taxable = account.accountType === AccountType.TAXABLE;
                                            const budget = getEffectiveTaxBudget(account);
                                            const realized = taxable ? metric?.ytdTax : undefined;
                                            const usage =
                                                taxable &&
                                                realized != null &&
                                                Number.isFinite(realized) &&
                                                budget.kind === 'amount'
                                                    ? getTaxBudgetUsage(budget.amount, realized)
                                                    : undefined;
                                            return (
                                                <PolicyTableRow key={account.accountGUID}>
                                                    <PolicyTableCell>
                                                        <div className="font-medium">
                                                            {account.accountName.plainText}
                                                        </div>
                                                        <div className="text-cocoa-500 mt-1 text-xs">
                                                            {formatAccountType(account.accountType)}
                                                        </div>
                                                    </PolicyTableCell>
                                                    <PolicyTableCell className="text-right tabular-nums">
                                                        {!taxable ? (
                                                            '—'
                                                        ) : budget.kind === 'unlimited' ? (
                                                            'Unlimited'
                                                        ) : budget.kind === 'amount' ? (
                                                            currency(budget.amount)
                                                        ) : (
                                                            <>
                                                                <div>Not configured</div>
                                                                <div className="text-cocoa-500 mt-1 text-xs">
                                                                    Effective budget: {currency(0)}
                                                                </div>
                                                            </>
                                                        )}
                                                    </PolicyTableCell>
                                                    <PolicyTableCell className="text-right tabular-nums">
                                                        {taxable && metricsQuery.isPending
                                                            ? 'Loading…'
                                                            : currency(realized)}
                                                    </PolicyTableCell>
                                                    <PolicyTableCell className="text-right tabular-nums">
                                                        {usage == null ? (
                                                            '—'
                                                        ) : (
                                                            <>
                                                                <div className="flex items-center justify-end gap-3">
                                                                    <Progress
                                                                        value={usage.percent}
                                                                        aria-label={`${account.accountName.plainText} tax budget used`}
                                                                        aria-valuemin={0}
                                                                        aria-valuemax={100}
                                                                        aria-valuenow={usage.percent}
                                                                        aria-valuetext={`${usage.percent}%${usage.overBudget ? ', over budget' : ''}`}
                                                                        className="[&>div]:bg-seafoam-600 h-1.5 w-28 shrink-0 bg-stone-300"
                                                                    />
                                                                    <span className="text-cocoa-600 w-10 shrink-0">
                                                                        {usage.percent}%
                                                                    </span>
                                                                </div>
                                                                {usage.overBudget && (
                                                                    <div className="text-destructive mt-1 text-xs">
                                                                        Over budget
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </PolicyTableCell>
                                                    <PolicyTableCell className="text-right tabular-nums">
                                                        {taxable ? rate(account.shortTermCapGainsTaxRate) : '—'}
                                                    </PolicyTableCell>
                                                    <PolicyTableCell className="text-right tabular-nums">
                                                        {taxable ? rate(account.longTermCapGainsTaxRate) : '—'}
                                                    </PolicyTableCell>
                                                </PolicyTableRow>
                                            );
                                        })}
                                    </PolicyTableBody>
                                </PolicyTable>
                            </div>
                        )}
                    </Card>
                    {taxableAccounts.length > 0 && metricsQuery.isLoadingError && (
                        <PolicyQueryError
                            message="Couldn’t load realized tax. Budget settings are still available."
                            queries={[metricsQuery]}
                        />
                    )}
                    <Card className="flex flex-row items-center gap-3.5 rounded-[10px] border-stone-200 px-6 py-[22px] shadow-none">
                        <LuScissors
                            aria-hidden="true"
                            className="text-cocoa-500 size-10 shrink-0 rounded-lg bg-stone-100 p-2"
                        />
                        <div className="flex-1">
                            <h3 className="text-cocoa-1000 text-[15px] font-medium">Tax-loss harvesting</h3>
                            <p className="text-cocoa-500 mt-1 text-sm">
                                Harvest losses and replace harvested assets with similar alternatives.
                            </p>
                        </div>
                        <span className="text-cocoa-700 rounded-[5px] bg-stone-100 px-2 py-1 text-xs font-medium">
                            {harvesting.text}
                        </span>
                    </Card>
                    <p className="text-cocoa-500 text-xs">
                        Realized tax is not realized P&amp;L. Tax does not apply to tax-deferred or tax-exempt accounts.
                    </p>
                </>
            )}
        </div>
    );
};
